'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { useUpdateProduct, useProductDetails, type UpdateProductInput } from '@/lib/api/products';
import { ProductInformationForm, ProductSettingsForm } from '@/components/form/product';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { updateProductSchema, type UpdateProductFormData } from './validation';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

const parseProductToFormData = (product: any): UpdateProductFormData => ({
  name: product.name || '',
  description: product.description || null,
  unitLabel: product.unitLabel || null,
  accountingCode: product.accountingCode || null,
  taxCategory: product.taxCategory || null,
  mappedTaxCategoryId: product.mappedTaxCategory?.id ?? null,
  active: product.active ?? true,
  isShippable: product.isShippable ?? true,
  isTaxable: product.isTaxable ?? true,
  autoSync: product.autoSync ?? true,
});

const EditProductPageInner = () => {
  const { productId } = useParams<{ id: string; productId: string }>();
  const { t } = useTranslation(['products', 'common']);
  const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
  const { toast } = useToast();

  if (!productId) {
    throw new Error('Store ID and Product ID are required');
  }

  const { data, isLoading, error } = useProductDetails([productId]);
  const updateProductMutation = useUpdateProduct();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

  const product = data?.[0];
  const formData = product ? parseProductToFormData(product) : null;

  const methods = useForm<UpdateProductFormData>({
    defaultValues: formData || {
      name: '',
      description: null,
      unitLabel: null,
      accountingCode: null,
      taxCategory: null,
      mappedTaxCategoryId: null,
      active: true,
      isShippable: true,
      isTaxable: true,
      autoSync: true,
    },
    resolver: zodResolver(updateProductSchema),
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = methods;

  React.useEffect(() => {
    if (product) {
      reset(parseProductToFormData(product));
    }
  }, [product, reset]);

  const handleUpdateProduct = async (data: UpdateProductFormData) => {
    try {
      setFormError(null);

      const input: UpdateProductInput = {
        productId,
        updatedData: {
          ...data,
        },
      };
      await updateProductMutation.mutateAsync(input);

      toast({
        title: t('productUpdated'),
        description: t('productUpdatedSuccess'),
      });

      navigate(storeRoutes.PRODUCT_DETAILS(productId));
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);

      if (!parsedError.isValidationError) {
        toast({
          title: t('updateError'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="text-muted-foreground mt-2 text-sm">
              {t('loading')}
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mx-auto py-8">
        <ErrorDisplay error={parseTRPCError(error)} />
      </div>
    );
  }
  if (!product) {
    return (
      <div className="container mx-auto py-8">
        <p className="text-red-500">{t('productNotFound')}</p>
      </div>
    );
  }
  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleUpdateProduct)}>
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-4">
                <BackButton to={storeRoutes.PRODUCT_DETAILS(productId)} />
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">{product.name}</h1>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('editProduct')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(storeRoutes.PRODUCT_DETAILS(productId))}
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting || updateProductMutation.isPending}>
                  {isSubmitting || updateProductMutation.isPending
                    ? t('updating')
                    : t('saveChanges')}
                </Button>
              </div>
            </div>

            {/* Error Display */}
            {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

            {/* Main Content */}
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              {/* Main content - 2/3 width */}
              <div className="space-y-6 lg:col-span-2">
                <ProductInformationForm />
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="space-y-6">
                <ProductSettingsForm />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export const EditProductPage = withRequiredPermission(EditProductPageInner, PERMISSIONS.STORES_MANAGE);
export default EditProductPage;
